import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { DealStage } from './constants';
import type { Deal, DealInput, LostReasonInput } from './types';

export const dealKeys = {
  all: ['deals'] as const,
  lists: () => [...dealKeys.all, 'list'] as const,
  list: (params: api.DealListParams) => [...dealKeys.lists(), params] as const,
  detail: (id: string | number) => [...dealKeys.all, 'detail', id] as const,
};

export function useDeals(params: api.DealListParams) {
  return useQuery({
    queryKey: dealKeys.list(params),
    queryFn: () => api.listDeals(params),
    placeholderData: keepPreviousData,
  });
}

export function useDeal(id: string | number) {
  return useQuery({
    queryKey: dealKeys.detail(id),
    queryFn: () => api.getDeal(id),
    enabled: Boolean(id),
  });
}

export function useCreateDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DealInput) => api.createDeal(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dealKeys.lists() });
      toast.success('Deal created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateDeal(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<DealInput>) => api.updateDeal(id, input),
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: dealKeys.lists() });
      qc.setQueryData(dealKeys.detail(id), deal);
      toast.success('Deal updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useArchiveDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.archiveDeal(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dealKeys.lists() });
      toast.success('Deal archived');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRestoreDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.restoreDeal(id),
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: dealKeys.lists() });
      qc.setQueryData(dealKeys.detail(deal.public_id), deal);
      toast.success('Deal restored');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function usePurgeDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.purgeDeal(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dealKeys.lists() });
      toast.success('Deal permanently deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

/**
 * Kanban drag-drop: change the deal's stage with an **optimistic** cache update
 * so the card moves instantly. On error we roll back the snapshot.
 */
export function useSetDealStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      stage,
      lost,
    }: {
      id: string | number;
      stage: DealStage;
      lost?: LostReasonInput;
    }) => api.setDealStage(id, stage, lost),
    onMutate: async ({ id, stage }) => {
      // Snapshot every cached deals list so we can roll back per key.
      await qc.cancelQueries({ queryKey: dealKeys.lists() });
      const snapshots = qc.getQueriesData<{ results: Deal[] } | undefined>({
        queryKey: dealKeys.lists(),
      });
      for (const [key, page] of snapshots) {
        if (!page?.results) continue;
        qc.setQueryData(key, {
          ...page,
          // Callers pass either key (the board passes the numeric id).
          results: page.results.map((d) =>
            d.public_id === id || d.id === id ? { ...d, stage } : d,
          ),
        });
      }
      return { snapshots };
    },
    onError: (err: ApiError, _vars, ctx) => {
      // Roll back.
      for (const [key, page] of ctx?.snapshots ?? []) {
        qc.setQueryData(key, page);
      }
      toast.error(err.message);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: dealKeys.lists() });
    },
  });
}
